import { Platform } from '@angular/cdk/platform';
import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { ShortlistService } from '@rma/public/compare-agent/private-api/da-compare-agent';
import { CompareTrayContainerComponent } from '@rma/public/compare-agent/public-api/compare-tray';
import { FeatureFlagService, TempFeatureFlags } from '@rma/public/generic/util/feature-flags';
import { WrongRegionModalService } from '@rma/public/generic/wrong-region';
import { FooterComponent } from '@rma/public/site/private/orc-footer';
import { SiteHeaderComponent } from '@rma/public/site/private/orc-header';
import { catchError, combineLatest, filter, map, mergeMap, Observable, of } from 'rxjs';
import { LazyLoadThirdPartyScriptsService } from '../util-lazy-load-scripts/lazy-load-scripts';
@Component({
  selector: 'rma-root',
  templateUrl: './rma-layout.component.html',
  styleUrls: ['./rma-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LazyLoadThirdPartyScriptsService],
  imports: [AsyncPipe, CompareTrayContainerComponent, FooterComponent, MatProgressSpinnerModule, RouterModule, SiteHeaderComponent],
})
export class RmaLayoutComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly platform = inject(Platform);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly shortlistService = inject(ShortlistService);

  // Resolved here rather than inside hasTray: resolveAgents() creates effects, which a computed
  // cannot do. Memoised, so this joins the tray's own resolution instead of starting a second one.
  private readonly shortlistAgents = this.shortlistService.resolveAgents();

  // angular scrollPositionRestoration can have issues in non blink browsers
  protected readonly scrollSmooth = this.platform.BLINK;

  // Resolved agents rather than shortlist codes: a code that fails to resolve renders no tray, and
  // space reserved against it would never be released.
  protected readonly hasTray = computed(() => this.shortlistAgents.agents().length > 0);

  protected isLoading = false;
  protected basicHeader$: Observable<boolean>;
  protected fullWidthHeader$: Observable<boolean>;
  protected showFooter$: Observable<boolean>;
  protected showHeader$: Observable<boolean>;
  protected showMarketingNav$: Observable<boolean>;
  protected renownedBranding$: Observable<boolean>;
  protected loginHeader$: Observable<boolean>;
  protected showCompareTray$: Observable<boolean>;

  constructor() {
    const lazyLoad3rdPartyScripts = inject(LazyLoadThirdPartyScriptsService);
    const wrongRegionModalService = inject(WrongRegionModalService);
    afterNextRender(() => {
      lazyLoad3rdPartyScripts.initialize();
      wrongRegionModalService.initialise();
    });
  }

  ngOnInit(): void {
    const routeData$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data),
      catchError(() =>
        of({
          hideFooter: false,
          hideHeader: false,
          basicHeader: false,
          fullWidthHeader: false,
          showMarketingNav: false,
          renownedBranding: false,
          loginHeader: false,
          showCompareTray: false,
        }),
      ),
      rmaShareReplay(),
      takeUntilDestroyed(this.destroyRef),
    );

    this.showFooter$ = routeData$.pipe(map(({ hideFooter }) => !hideFooter));
    this.showHeader$ = routeData$.pipe(map(({ hideHeader }) => !hideHeader));
    this.basicHeader$ = routeData$.pipe(map(({ basicHeader }) => !!basicHeader));
    this.fullWidthHeader$ = routeData$.pipe(map(({ fullWidthHeader }) => !!fullWidthHeader));
    this.showMarketingNav$ = routeData$.pipe(map(({ showMarketingNav }) => !!showMarketingNav));
    this.renownedBranding$ = routeData$.pipe(map(({ renownedBranding }) => !!renownedBranding));
    this.loginHeader$ = routeData$.pipe(map(({ loginHeader }) => !!loginHeader));
    this.showCompareTray$ = combineLatest([
      routeData$.pipe(map(({ showCompareTray }) => !!showCompareTray)),
      this.featureFlagService.getFeature$(TempFeatureFlags.CompareTray, false),
    ]).pipe(map(([routeOptsIn, flagOn]) => routeOptsIn && flagOn));

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => this.setLoading(e));
  }

  // The tray re-emits rather than navigating, so its route belongs to whoever mounts it.
  protected onCompare(): void {
    this.router.navigate(['/account/compare-agents']);
  }

  private setLoading(event: Event): void {
    if (event instanceof NavigationStart) {
      this.isLoading = true;
    } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      // Set loading state to false in these events to hide the spinner in case a request fails
      this.isLoading = false;
    }
  }
}
